import React, { useEffect, useRef, useCallback, useState } from "react";
import ReactTooltip, { Place } from "react-tooltip";
import Text from "../Text";
import { MEDIA_QUERIES } from "../../styles";
import { ColorType, theme } from "../../styles/theme";
import { useMediaQuery } from "react-responsive";
import styled, { createGlobalStyle } from "styled-components";
import Icon from "@icon-park/react/es/all";

const GlobalTooltipStyle = createGlobalStyle`
  .__react_component_tooltip {
    opacity: 1 !important;
  }
  .__react_component_tooltip.show {
    opacity: 1 !important;
  }
`;

const LabelText = styled(Text)`
  cursor: pointer;
`;

const StyledIcon = styled(Icon)`
  cursor: pointer;
`;

const StyledTooltip = styled(ReactTooltip as any)`
  background-color: ${({ backgroundColor }) => backgroundColor} !important;
  border-radius: 8px !important;
  padding: 16px !important;
  max-width: 300px !important;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1) !important;
  z-index: 1000 !important;
`;

const TitleText = styled(Text)`
  margin-bottom: 8px;
`;

const TooltipText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TooltipWrapper = styled.div`
  position: relative;
`;

const StyledCross = styled(Icon)`
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
`;

type TooltipRef = { tooltipRef: null } | null;

interface TooltipProps {
  content: React.ReactNode;
  textColor?: ColorType;
  label?: string;
  labelFont?: string;
  labelColor?: ColorType;
  title?: string;
  titleColor?: ColorType;
  id?: string;
  backgroundColor?: ColorType;
  openOnMount?: boolean;
  icon?: string;
  iconColor?: ColorType;
  place?: Place;
}

const Tooltip: React.FC<TooltipProps> = ({
  id,
  icon,
  iconColor = "SLATE450",
  backgroundColor = "WHITE",
  content,
  textColor = "SLATE500",
  label = "Important Information",
  labelFont = "H14",
  labelColor = "SLATE800",
  openOnMount,
  title,
  titleColor = "SLATE500",
  place = "bottom",
}) => {
  const [isTooltipOpen, setTooltipOpen] = useState(false);
  const tooltipWrapperRef = useRef(null);
  const tooltip = useRef(null);
  const isMobile = useMediaQuery({ query: MEDIA_QUERIES.PHONE });

  useEffect(() => {
    openOnMount && onTooltipTriggerClick();
  }, [tooltipWrapperRef.current]);

  const hideTooltipHandler = useCallback(() => {
    const tooltipWrapperEl = tooltipWrapperRef.current;
    if (tooltipWrapperEl) {
      ReactTooltip.hide(tooltipWrapperEl);
      setTooltipOpen(false);
      document.removeEventListener("click", hideTooltipHandler);
    }
  }, []);

  const onTooltipTriggerClick = useCallback(() => {
    const tooltipWrapperEl = tooltipWrapperRef.current;
    if (tooltipWrapperEl) {
      if (isTooltipOpen) {
        ReactTooltip.hide(tooltipWrapperEl);
        setTooltipOpen(false);
      } else {
        ReactTooltip.show(tooltipWrapperEl);
        setTooltipOpen(true);
        document.addEventListener("click", hideTooltipHandler);
      }
    }
  }, [isTooltipOpen]);

  useEffect(() => {
    return () => {
      document.removeEventListener("click", hideTooltipHandler);
    };
  }, []);

  const renderContent = () => {
    if (typeof content === "string") {
      return (
        <Text type="P14" color={textColor}>
          {content}
        </Text>
      );
    }

    return content;
  };

  return (
    <section>
      <GlobalTooltipStyle />
      <div
        data-tip
        data-for={id}
        ref={tooltipWrapperRef}
        data-event="click"
        onClick={onTooltipTriggerClick}
      >
        {icon ? (
          <StyledIcon type={icon} size={16} fill={theme.colors[iconColor]} />
        ) : (
          <LabelText type={labelFont} color={labelColor}>
            {label}
          </LabelText>
        )}
      </div>
      <StyledTooltip
        ref={tooltip}
        overridePosition={(position: { top: number; left: number }) => ({
          top: position.top,
          left: window.innerWidth < 600 ? 4 : position.left,
        })}
        backgroundColor={theme.colors[backgroundColor]}
        id={id}
        place={place}
        type="light"
        isCapture
        clickable
        globalEventOff="hover"
        effect="solid"
        arrowColor={isMobile ? "transparent" : theme.colors[backgroundColor]}
        getContent={() => (
          <TooltipWrapper>
            <StyledCross
              type="Close"
              fill={theme.colors.SLATE500}
              size={14}
              onClick={() => {
                const current: TooltipRef = tooltip.current;
                current!.tooltipRef = null;
                onTooltipTriggerClick();
              }}
            />
            <TooltipText>
              {title && (
                <TitleText type="H14" color={titleColor}>
                  {title}
                </TitleText>
              )}
              {renderContent()}
            </TooltipText>
          </TooltipWrapper>
        )}
      />
    </section>
  );
};

export default Tooltip;
